import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog.js';
import { useDeleteTag, useTags } from '../../common/admin-hooks.js';
import { TagChip } from './TagChip.js';
import { TagEditorDialog } from './TagEditorDialog.js';

/**
 * Top-level "Manage Tags" surface — lists every license tag with
 * edit + delete affordances, plus a "New Tag" button at the top.
 * Replaces the previous behaviour where the Presentation rail's
 * "Manage Tags" button opened `TagEditorDialog` straight in
 * create mode — operators had no path to delete or rename an
 * existing tag without leaving the presentation surface.
 *
 * Wires:
 *   • `useTags()` for the license-scoped tag list.
 *   • Click "New Tag" → opens `TagEditorDialog` in create mode.
 *   • Click an edit pencil on a row → opens `TagEditorDialog`
 *     in edit mode (pre-filled from the row).
 *   • Click a delete trash on a row → `window.confirm` → calls
 *     `useDeleteTag().remove(id)` (DELETE /v1/tags/{id}).
 *
 * The delete is a hard delete: the engine cascades the junction
 * rows in `fusion_scenario__scenario_tag` so every scenario that
 * had the tag attached loses it. Confirm dialog wording reflects
 * this so operators don't expect a soft / undoable delete.
 *
 * Mirrors the legacy `wf-audit/src/slices/presentation/components/
 * presentationFilter/ManageTags.tsx` list-first layout. Reused by
 * `PresentationScenariosRail`'s "Manage Tags" entry point.
 */
export interface TagsManageDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

interface TagOption {
  id: number;
  title: string;
  description: string | null;
  color: string | null;
  icon: string | null;
}

export function TagsManageDialog({ open, onOpenChange }: TagsManageDialogProps): JSX.Element {
  const { data: tagRows = [], isLoading } = useTags();
  const { remove, isPending: deleting } = useDeleteTag();
  // `null` = closed, `'create'` = TagEditorDialog in create mode,
  // tag option = TagEditorDialog in edit mode for that tag.
  const [editorTarget, setEditorTarget] = useState<TagOption | 'create' | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const tags = useMemo<ReadonlyArray<TagOption>>(() => {
    const out = tagRows.flatMap<TagOption>((r) => {
      if (!r || typeof r !== 'object') return [];
      const id = typeof r.id === 'number' ? r.id : null;
      const title = typeof r.title === 'string' ? r.title : null;
      if (id === null || title === null) return [];
      return [
        {
          id,
          title,
          description: typeof r.description === 'string' ? r.description : null,
          color: typeof r.color === 'string' ? r.color : null,
          icon: typeof r.icon === 'string' ? r.icon : null,
        },
      ];
    });
    // Alphabetise so the list order is stable across reloads and
    // matches the picker dropdowns operators see elsewhere.
    return out.sort((a, b) => a.title.localeCompare(b.title));
  }, [tagRows]);

  async function handleDelete(tag: TagOption): Promise<void> {
    const ok = window.confirm(
      `Delete tag "${tag.title}"? This removes the tag from every scenario that currently has it attached and cannot be undone.`,
    );
    if (!ok) return;
    setPendingDeleteId(tag.id);
    try {
      await remove(String(tag.id));
    } catch {
      // Toasts surface the error; clear the spinner regardless.
    } finally {
      setPendingDeleteId((id) => (id === tag.id ? null : id));
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground">
          <DialogHeader>
            <DialogTitle>Manage Tags</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditorTarget('create')}
              className="w-full justify-center border-foreground/15 text-foreground/85 hover:border-foreground/40 hover:text-foreground"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              New Tag
            </Button>

            {isLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-foreground/45">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading tags…
              </div>
            ) : tags.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-foreground/50">
                No tags yet. Create one above to start grouping scenarios.
              </p>
            ) : (
              <ul className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
                {tags.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-center gap-2 rounded-md border border-foreground/10 bg-foreground/[0.03] px-2.5 py-1.5"
                  >
                    <span className="min-w-0 flex-1">
                      <TagChip tag={t} size="md" />
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditorTarget(t)}
                      className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
                      aria-label={`Edit tag ${t.title}`}
                      title={`Edit tag ${t.title}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void handleDelete(t);
                      }}
                      disabled={deleting && pendingDeleteId === t.id}
                      className="rounded p-1 text-foreground/50 hover:bg-rose-500/15 hover:text-rose-500 disabled:opacity-50"
                      aria-label={`Delete tag ${t.title}`}
                      title={`Delete tag ${t.title}`}
                    >
                      {deleting && pendingDeleteId === t.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TagEditorDialog
        open={editorTarget !== null}
        onOpenChange={(next) => {
          if (!next) setEditorTarget(null);
        }}
        initial={
          editorTarget && editorTarget !== 'create'
            ? {
                id: editorTarget.id,
                title: editorTarget.title,
                description: editorTarget.description,
                color: editorTarget.color,
                icon: editorTarget.icon,
              }
            : null
        }
        onSaved={() => setEditorTarget(null)}
      />
    </>
  );
}
